'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { ButtonShadcn } from '@/components/ui/button-shadcn';
import { PiArrowUpRightBold, PiListLight, PiX } from 'react-icons/pi';
import Image from 'next/image';
import { getStaticAssetPath, getLocalizedPath } from '@/lib/utils';
import { IconButton } from '@/components/ui/button';
import { useLanguageFromPath } from '@/hooks/useLanguageFromPath';

const MainHeader = () => {
    const [isOpen, setIsOpen] = useState(false);
  const lang = useLanguageFromPath();
    const { t } = useTranslation();

  // Get translations (async updates in background)
  const startLearningLabel = t('homePage.startLearning');
  const aboutLabel = t('homePage.about');
  const homeLabel = t('homePage.home');

  // Derive links from language
  const homeHref = getLocalizedPath('/', lang);
  const aboutHref = getLocalizedPath('/about', lang);
  const practiceHref = getLocalizedPath('/practice', lang) + '?m=iAwxCJZywemj';

  return (
    <>
      <header className='w-full border-b py-4 lg:px-8 px-6 flex items-center justify-between sticky top-0 bg-white z-50'>
        <div className='flex items-center gap-2'>
          <Image
            src={getStaticAssetPath('/static/siteLogo.svg')}
            alt='DarPiano Logo'
            className='h-9 w-auto'
            width={36}
            height={36}
          />
          <Link
            href={homeHref}
            className='text-3xl font-light'
          >
            DarPiano
          </Link>
        </div>

        {/* Desktop Menu */}
        <div className='hidden lg:flex items-center gap-4'>
          <Link
            href={homeHref}
            className='text-base hover:underline bg-transparent border-none cursor-pointer'
          >
            {homeLabel}
          </Link>
          <Link
            href={aboutHref}
            className='text-base hover:underline bg-transparent border-none cursor-pointer'
          >
            {aboutLabel}
          </Link>
          <ButtonShadcn asChild variant='blue' size='sm'>
            <Link
              href={practiceHref}
              className='flex items-center gap-1'
            >
              {startLearningLabel}
              <PiArrowUpRightBold />
            </Link>
          </ButtonShadcn>
        </div>

        <div className='flex lg:hidden flex-row gap-4'>
          {/* Tablet Start Learning Button */}
          <div className='hidden md:flex lg:hidden'>
            <ButtonShadcn asChild variant='blue' size='sm'>
              <Link
                href={practiceHref}
                className='flex items-center gap-1'
              >
                {startLearningLabel}
                <PiArrowUpRightBold />
              </Link>
            </ButtonShadcn>
          </div>

          {/* Mobile/Tablet Burger Button */}
          <IconButton
            onClick={() => setIsOpen(true)}
            aria-label='Open menu'
            className='lg:hidden'
          >
            <PiListLight size={28} />
          </IconButton>
        </div>
      </header>

      {/* Mobile/Tablet Full Screen Menu */}
      {isOpen && (
        <div className='lg:hidden fixed inset-0 z-50 bg-white flex flex-col'>
          {/* Header */}
          <div className='flex items-center justify-between py-4 px-6 border-b'>
            <div className='flex items-center gap-2'>
              <Image
                src={getStaticAssetPath('/static/siteLogo.svg')}
                alt='DarPiano Logo'
                className='h-9 w-auto'
                width={36}
                height={36}
              />
              <Link
                href={homeHref}
                className='text-3xl font-light'
              >
                DarPiano
              </Link>
            </div>
            <IconButton
              onClick={() => setIsOpen(false)}
              aria-label='Close menu'
              className='lg:hidden'
            >
              <PiX size={28} />
            </IconButton>
          </div>

          {/* Menu Content */}
          <div className='flex-1 flex flex-col items-center justify-start gap-6 p-8'>
            <ButtonShadcn asChild variant='blue' size='lg'>
              <Link
                href={practiceHref}
                onClick={() => setIsOpen(false)}
                className='flex items-center gap-2'
              >
                {startLearningLabel}
                <PiArrowUpRightBold />
              </Link>
            </ButtonShadcn>
            <Link
              href={homeHref}
              onClick={() => setIsOpen(false)}
              className='text-2xl hover:underline'
            >
              {homeLabel}
            </Link>
            <Link
              href={aboutHref}
              onClick={() => setIsOpen(false)}
              className='text-2xl hover:underline'
            >
              {aboutLabel}
            </Link>
          </div>
        </div>
      )}
    </>
  );
};

export default MainHeader;
